import { DOCUMENT } from '@angular/common';
import { Directive, ElementRef, OnDestroy, inject } from '@angular/core';

/** How long to wait for the on-screen keyboard to finish opening before scrolling (ms). */
const KEYBOARD_SETTLE_MS = 350;

/**
 * Keeps a focused form control visible above the on-screen keyboard (OEM-7023, FR-054).
 *
 * iPadOS Safari does not resize the layout viewport when the keyboard opens, so a field
 * near the bottom can end up hidden behind it. On touch input only, this waits for the
 * keyboard to settle, then scrolls the host to the centre of the visible area if it is
 * not already fully visible. Mouse and keyboard users are unaffected.
 *
 * Applied through `hostDirectives` on lib-anc-form-field, lib-anc-select and
 * lib-anc-date-picker, so every usage gets it without template changes.
 */
@Directive({
    selector: '[libAncKeepInView]',
    standalone: true,
    host: {
        '(focusin)': 'onFocusIn()'
    }
})
export class AncKeepInViewDirective implements OnDestroy {
    private readonly host = inject(ElementRef<HTMLElement>);
    private readonly document = inject(DOCUMENT);
    private settleTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private removeResizeListener: (() => void) | null = null;

    onFocusIn(): void {
        const view = this.document.defaultView;
        if (!view?.matchMedia?.('(pointer: coarse)').matches) {
            return;
        }
        this.cancelPending();

        const visualViewport = view.visualViewport;
        const scrollNow = (): void => {
            this.cancelPending();
            view.requestAnimationFrame(() => this.scrollIntoViewIfHidden());
        };

        // Scroll as soon as the keyboard resizes the visual viewport, or after a short
        // fallback delay (keyboard already open, or no visualViewport support).
        if (visualViewport) {
            visualViewport.addEventListener('resize', scrollNow, { once: true });
            this.removeResizeListener = () => visualViewport.removeEventListener('resize', scrollNow);
        }
        this.settleTimeoutId = setTimeout(scrollNow, KEYBOARD_SETTLE_MS);
    }

    ngOnDestroy(): void {
        this.cancelPending();
    }

    private scrollIntoViewIfHidden(): void {
        const el = this.host.nativeElement;
        const view = this.document.defaultView;
        if (!view) {
            return;
        }
        const rect = el.getBoundingClientRect();
        const visibleTop = view.visualViewport?.offsetTop ?? 0;
        const visibleBottom = visibleTop + (view.visualViewport?.height ?? view.innerHeight);
        if (rect.top >= visibleTop && rect.bottom <= visibleBottom) {
            return;
        }
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
    }

    private cancelPending(): void {
        if (this.settleTimeoutId != null) {
            clearTimeout(this.settleTimeoutId);
            this.settleTimeoutId = null;
        }
        this.removeResizeListener?.();
        this.removeResizeListener = null;
    }
}
